const ROW = '[data-execution-row]';
const rowId = row => row.dataset.executionId || row.dataset.nodeId;
const HANDLE = '[data-execution-drag]';
const rows = list => [...list.querySelectorAll(ROW)];
const rowGroup = row => row.dataset.executionGroup || (row.classList.contains('done') ? 'completed' : 'current');

/** Save visible row positions, so removing/completing a row doesn't move its neighbors. */
export function captureExecutionScroll(list, scroller = list) {
  if (!list || !scroller) return null;
  const bounds = scroller.getBoundingClientRect();
  const top = bounds.top + scroller.clientTop;
  const focused = document.activeElement?.closest(HANDLE)?.closest(ROW);
  return {
    top: scroller.scrollTop,
    anchors: rows(list).filter(row => rowId(row) !== list.dataset.executionMoving)
      .map(row => ({ id: rowId(row), group: rowGroup(row), rect: row.getBoundingClientRect() }))
      .filter(row => row.rect.bottom > top && row.rect.top < top + scroller.clientHeight)
      // Prefer a readable row over the clipped tail of the preceding task.
      .sort((a, b) => Number(a.rect.top < top) - Number(b.rect.top < top))
      .map(row => ({ id: row.id, group: row.group, offset: row.rect.top - top })),
    focusId: focused && list.contains(focused) ? rowId(focused) : null,
    revealFocus: list.dataset.executionKeyboardMove === 'true',
  };
}

export function restoreExecutionScroll(list, saved, scroller = list) {
  if (!list || !saved || !scroller) return;
  const currentRows = new Map(rows(list).map(row => [rowId(row), row]));
  scroller.scrollTop = saved.top;
  if (saved.top > 0) {
    // A task newly moved to another group must not pull the viewport down with it.
    const anchor = saved.anchors.find(item => currentRows.has(item.id)
      && rowGroup(currentRows.get(item.id)) === item.group);
    if (anchor) {
      scroller.scrollTop += currentRows.get(anchor.id).getBoundingClientRect().top
        - scroller.getBoundingClientRect().top - scroller.clientTop - anchor.offset;
    }
  }
  const focusRow = currentRows.get(saved.focusId);
  focusRow?.querySelector(HANDLE)?.focus({ preventScroll: true });
  if (focusRow && saved.revealFocus) {
    const row = focusRow.getBoundingClientRect(), box = scroller.getBoundingClientRect();
    if (row.top < box.top) scroller.scrollTop += row.top - box.top;
    else if (row.bottom > box.bottom) scroller.scrollTop += row.bottom - box.bottom;
  }
}

/** Handle-only pointer reordering (mouse/touch), with keyboard and edge scrolling.
 * onMove returns true when it applies an order and triggers a list render.
 */
export function bindExecutionReorder(list, { scroller = list, isReadOnly, onMove }) {
  if (!list) return () => {};
  const abort = new AbortController();
  let drag = null, frame = null, marker = null;
  const listen = (type, fn, options = {}) => list.addEventListener(type, fn, { ...options, signal: abort.signal });
  const clearMarker = () => {
    marker?.classList.remove('execution-drop-before', 'execution-drop-after');
    marker = null;
  };
  function cancel() {
    const previous = drag;
    drag = null;
    cancelAnimationFrame(frame);
    frame = null;
    clearMarker();
    previous?.row.classList.remove('execution-dragging');
    if (previous?.handle.hasPointerCapture(previous.pointerId)) previous.handle.releasePointerCapture(previous.pointerId);
    delete list.dataset.executionMoving;
  }
  function markTarget() {
    clearMarker();
    drag.target = null;
    const box = scroller.getBoundingClientRect();
    if (drag.x < box.left - 30 || drag.x > box.right + 30) return;
    const candidates = rows(list).filter(row => row !== drag.row && rowGroup(row) === drag.group);
    const target = candidates.find(row => {
      const rect = row.getBoundingClientRect();
      return drag.y < rect.top + rect.height / 2;
    }) || candidates.at(-1);
    if (!target) return;
    const rect = target.getBoundingClientRect();
    drag.after = drag.y >= rect.top + rect.height / 2;
    drag.target = rowId(target);
    marker = target;
    target.classList.add(drag.after ? 'execution-drop-after' : 'execution-drop-before');
  }
  function autoScroll() {
    if (!drag?.started || !list.isConnected || isReadOnly()) { cancel(); return; }
    const box = scroller.getBoundingClientRect();
    if (drag.x >= box.left - 30 && drag.x <= box.right + 30) {
      const edge = Math.min(32, box.height / 4);
      const amount = drag.y < box.top + edge ? -Math.min(10, (box.top + edge - drag.y) / 3)
        : drag.y > box.bottom - edge ? Math.min(10, (drag.y - box.bottom + edge) / 3) : 0;
      if (amount) { scroller.scrollTop += amount; markTarget(); }
    }
    frame = requestAnimationFrame(autoScroll);
  }
  listen('pointerdown', event => {
    const handle = event.target.closest(HANDLE);
    if (!handle || event.button !== 0 || isReadOnly()) return;
    event.preventDefault();
    cancel();
    const row = handle.closest(ROW);
    drag = { handle, row, pointerId: event.pointerId, nodeId: rowId(row),
      group: rowGroup(row), startY: event.clientY, startX: event.clientX,
      x: event.clientX, y: event.clientY, started: false, target: null };
    handle.setPointerCapture(event.pointerId);
  });
  listen('pointermove', event => {
    if (!drag || event.pointerId !== drag.pointerId) return;
    if (isReadOnly()) { cancel(); return; }
    drag.x = event.clientX; drag.y = event.clientY;
    if (!drag.started && Math.hypot(drag.x - drag.startX, drag.y - drag.startY) < 5) return;
    event.preventDefault();
    if (!drag.started) {
      drag.started = true;
      drag.row.classList.add('execution-dragging');
      list.dataset.executionMoving = drag.nodeId;
      frame = requestAnimationFrame(autoScroll);
    }
    markTarget();
  });
  listen('pointerup', event => {
    if (!drag || event.pointerId !== drag.pointerId) return;
    const moved = drag;
    cancel();
    if (!moved.started || !moved.target || isReadOnly()) return;
    list.dataset.executionMoving = moved.nodeId;
    try {
      // A mobile render runs next frame. Keep the moved row excluded from
      // the scroll anchors until that render captures the old list.
      if (!onMove(moved.nodeId, moved.target, moved.after)) delete list.dataset.executionMoving;
    } catch (error) { delete list.dataset.executionMoving; throw error; }
  });
  listen('pointercancel', () => { if (drag) cancel(); });
  listen('lostpointercapture', () => { if (drag) cancel(); });
  listen('keydown', event => {
    if (event.key === 'Escape' && drag) { event.preventDefault(); event.stopPropagation(); cancel(); return; }
    const handle = event.target.closest(HANDLE);
    if (!handle || !['ArrowUp', 'ArrowDown'].includes(event.key)) return;
    event.preventDefault(); event.stopPropagation();
    if (isReadOnly()) return;
    const row = handle.closest(ROW);
    const group = rows(list).filter(item => rowGroup(item) === rowGroup(row));
    const after = event.key === 'ArrowDown';
    const target = group[group.indexOf(row) + (after ? 1 : -1)];
    if (!target) return;
    list.dataset.executionMoving = rowId(row);
    list.dataset.executionKeyboardMove = 'true';
    try {
      if (onMove(rowId(row), rowId(target), after)) return;
    } catch (error) {
      delete list.dataset.executionMoving;
      delete list.dataset.executionKeyboardMove;
      throw error;
    }
    delete list.dataset.executionMoving;
    delete list.dataset.executionKeyboardMove;
  });
  listen('click', event => {
    if (event.target.closest(HANDLE)) { event.preventDefault(); event.stopPropagation(); }
  });
  return () => { cancel(); abort.abort(); };
}
